import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import CircleIcon from "@mui/icons-material/Circle";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { format, formatDistanceToNow } from "date-fns";
import type React from "react";
import { useMemo, useState } from "react";
import { useApiQuery } from "../../hooks";
import type { APIKey, AppConfig, AppConfigType } from "../../types";
import { CreatedByColumn } from "../created-by-column";
import { DataGrid } from "../data-grid";
import { DeleteModal } from "../delete-dialog";

interface AppConfigTableProps {
  configs: AppConfig[];
  isLoading?: boolean;
  onEdit: (config: AppConfig) => void;
  onDelete: (config: AppConfig) => void;
  onCreate: () => void;
  isDeleting?: boolean;
}

const AppConfigActionColumn = ({
  row,
  onEdit,
  onDelete,
}: GridRenderCellParams<AppConfig> & {
  onEdit: (config: AppConfig) => void;
  onDelete: (config: AppConfig) => void;
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ height: "100%" }}>
      <Tooltip title="View/Edit config">
        <IconButton aria-label="edit" onClick={() => onEdit(row)} size="small">
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Delete config">
        <IconButton
          aria-label="delete"
          onClick={() => setDeleteConfirmOpen(true)}
          size="small"
          color="error"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <DeleteModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={() => {
          onDelete(row);
          setDeleteConfirmOpen(false);
        }}
        title="Delete Application Config"
        itemName={row.name}
        itemType="application config"
        message={`Are you sure you want to delete the application config "${row.name}"?`}
      />
    </Stack>
  );
};

const getConfigTypeColor = (type: AppConfigType | undefined) => {
  switch (type) {
    case "production":
      return "error";
    case "staging":
      return "warning";
    case "development":
      return "info";
    case "local":
      return "success";
    default:
      return "default";
  }
};

const getConfigTypeLabel = (type: AppConfigType | undefined) => {
  switch (type) {
    case "production":
      return "production";
    case "staging":
      return "staging";
    case "development":
      return "development";
    case "local":
      return "local";
    default:
      return type || "legacy";
  }
};

export const AppName = ({ appConfig }: { appConfig: AppConfig }) => {
  const { data } = useApiQuery<APIKey>(`/api-key/by-app-config/${appConfig.id}?only_get=true`);

  const lastUse = useMemo(() => {
    if (!data) return null;
    return data.last_used_at ? format(data.last_used_at, "MMM d, yyyy, h:mm a") : null;
  }, [data]);

  const isLive = useMemo(() => {
    if (!data) return false;
    return (
      appConfig.cfg_type === "local" &&
      data.last_used_at &&
      Date.now() - new Date(data.last_used_at).getTime() <
        // 5 minutes
        5 * 60 * 1000
    );
  }, [appConfig, data]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      width="100%"
      justifyContent="flex-start"
      height="100%"
      sx={{
        "@keyframes pulseGlow": {
          "0%": {
            opacity: 1,
            filter: "drop-shadow(0 0 1px #4caf50)",
          },
          "50%": {
            opacity: 0.75,
            filter: "drop-shadow(0 0 2px #4caf50) drop-shadow(0 0 3px #4caf50)",
          },
          "100%": {
            opacity: 1,
            filter: "drop-shadow(0 0 1px #4caf50)",
          },
        },
      }}
    >
      {isLive ? (
        <Tooltip title={`API key last used at ${lastUse}`}>
          <CircleIcon
            fontSize="small"
            sx={{
              color: "success.main",
              fontSize: "12px",
              animation: "pulseGlow 2s ease-in-out infinite",
            }}
          />
        </Tooltip>
      ) : lastUse ? (
        <Tooltip title={`API Key last used at ${lastUse}`}>
          <CircleIcon fontSize="small" sx={{ color: "gray", fontSize: "12px" }} />
        </Tooltip>
      ) : (
        <Tooltip title="API key never used">
          <CircleIcon
            fontSize="small"
            sx={{ color: "lightgray", fontSize: "12px", opacity: 0.5 }}
          />
        </Tooltip>
      )}
      <Typography variant="body2" noWrap fontWeight={500}>
        {appConfig.name}
      </Typography>
    </Stack>
  );
};

export const AppConfigTable: React.FC<AppConfigTableProps> = ({
  configs,
  isLoading = false,
  onEdit,
  onDelete,
  onCreate,
}) => {
  const columns = useMemo<GridColDef<AppConfig>[]>(() => {
    return [
      {
        field: "name",
        headerName: "Name",
        flex: 0.2,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams<AppConfig>) => (
          <Box alignItems="center" display="flex" height="100%" width="100%">
            <AppName appConfig={params.row} />
          </Box>
        ),
      },
      {
        field: "cfg_type",
        headerName: "Type",
        flex: 0.1,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<AppConfig>) => (
          <Box alignItems="center" display="flex" height="100%" width="100%">
            <Chip
              label={getConfigTypeLabel(params.row.cfg_type)}
              color={getConfigTypeColor(params.row.cfg_type)}
              size="small"
              variant="outlined"
            />
          </Box>
        ),
      },
      {
        field: "base_url",
        headerName: "Base URL",
        flex: 1,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams<AppConfig>) => (
          <Box alignItems="center" display="flex" height="100%" width="100%">
            <Typography variant="body2" noWrap color="text.secondary">
              {params.row.base_url || "—"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "login_username",
        headerName: "Login User",
        flex: 0.15,
        minWidth: 140,
        renderCell: (params: GridRenderCellParams<AppConfig>) => (
          <Box alignItems="center" display="flex" height="100%" width="100%">
            <Typography variant="body2" noWrap color="text.secondary">
              {params.row.login_username || "—"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "created_by",
        headerName: "Created By",
        flex: 0.15,
        minWidth: 140,
        renderCell: CreatedByColumn,
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 0.1,
        minWidth: 140,
        renderCell: (params: GridRenderCellParams<AppConfig>) => (
          <Box alignItems="center" display="flex" height="100%" width="100%">
            <Tooltip title={new Date(params.row.created_at).toLocaleString()}>
              <Typography variant="body2" noWrap color="text.secondary">
                {formatDistanceToNow(new Date(params.row.created_at), {
                  addSuffix: true,
                })}
              </Typography>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.1,
        minWidth: 100,
        sortable: false,
        renderCell: (params: GridRenderCellParams<AppConfig>) => (
          <AppConfigActionColumn {...params} onEdit={onEdit} onDelete={onDelete} />
        ),
      },
    ];
  }, [onDelete, onEdit]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Application Configurations</Typography>
        <Tooltip title="Add Configuration" placement="top">
          <span>
            <IconButton
              size="small"
              onClick={onCreate}
              disabled={isLoading}
              sx={{
                borderRadius: 2,
                p: 1.5,
                border: 0,
                color: "grey.800",
                backgroundColor: "transparent",
                "&:hover": {
                  borderColor: "info.dark",
                  backgroundColor: "grey.100",
                },
                "&:disabled": {
                  borderColor: "divider",
                  backgroundColor: "grey.100",
                  color: "grey.500",
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <DataGrid
        rows={configs}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        autoHeight
        customSx={{
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid",
            borderColor: "grey.200",
          },
        }}
      />
    </Box>
  );
};
